import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { UserPlusIcon } from '@heroicons/react/20/solid';
import vCardFactory from 'vcards-js';
import { LINK_TYPE } from '@utils/constants';
import { trackProfileDownload } from '@utils/analytics';

const profileDownloader = ({ textColor, user, optimizedAvatar, links, ownerId }) => {
  // Base64 vorbereiten
  const [base64String, setBase64String] = useState('');
  const [imageMimeType, setImageMimeType] = useState('');

  const generateBase64String = () => {
    return new Promise(async (resolve, reject) => {
      try {
        // cache: 'reload' – die Dashboard-Nav lädt dieselbe square-URL per
        // <img> ohne CORS; Safari bedient fetch() sonst aus diesem Cache-
        // Eintrag ohne CORS-Header ("Load failed"). reload holt die Antwort
        // frisch (mit Origin) und ersetzt den Cache-Eintrag.
        const response = await fetch(optimizedAvatar, { cache: 'reload' });
        if (!response.ok) {
          // SOS liefert für fehlende Objekte 403 – sonst landet die
          // Fehlerseite als "Foto" in der vCard.
          const httpError = new Error(`Avatar HTTP ${response.status}`);
          httpError.status = response.status;
          throw httpError;
        }
        const blob = await response.blob();

        // MIME-Type extrahieren (z.B. "image/jpeg" → "jpeg")
        const mimeType = blob.type.split('/')[1] || 'jpeg';
        setImageMimeType(mimeType);
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result.split(',')[1];
          setBase64String(base64Data);
          resolve({ base64: base64Data, mimeType });
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          reject(error);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error converting image:', error);
        reject(error);
      }
    });
  };

  // Download TXT File
  const downloadTxtFile = (vcfText) => {
    const element = document.createElement('a');

    const file = new Blob([vcfText], {
      type: 'text/vcard;charset=utf-8',
    });

    element.href = URL.createObjectURL(file);
    element.download = 'vCard.vcf';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
  };
  // VCard erstellen
  const CreateVCard = async () => {
    // Base64 Bild erstellen, wenn noch nicht vorhanden
    let base64Image = base64String;
    let mimeType = imageMimeType;

    if (!base64Image) {
      try {
        const result = await generateBase64String();
        base64Image = result.base64;
        mimeType = result.mimeType;
      } catch (error) {
        // vCard ohne Foto ist besser als gar kein Download – aber nie still:
        // dauerhafte Fälle (fehlendes Objekt, CORS) sollen in Sentry pro
        // Profil sichtbar werden.
        Sentry.captureException(error, {
          tags: { feature: 'vcard-avatar' },
          extra: { url: optimizedAvatar, status: error.status, ownerId },
        });
      }
    }

    //create a new vCard
    const vCard = vCardFactory();

    //set properties
    // zuerst User Properties
    vCard.firstName = user.firstName;
    vCard.lastName = user.lastName;
    vCard.title = user.title;
    vCard.organization = user.company;
    if (base64Image) {
      vCard.photo.embedFromString(base64Image, mimeType || 'jpeg');
    }
    // Link Inhalte

    links.forEach((item) => {
      switch (item.type) {
        case LINK_TYPE.ADRESSE_FIRMA:
          vCard.workAddress.label = item.title;
          vCard.workAddress.street = item.strasse;
          vCard.workAddress.city = item.ort;
          vCard.workAddress.postalCode = item.plz;
          break;
        case LINK_TYPE.ADRESSE_PRIVAT:
          vCard.homeAddress.label = item.title;
          vCard.homeAddress.street = item.strasse;
          vCard.homeAddress.city = item.ort;
          vCard.homeAddress.postalCode = item.plz;
          break;
        case LINK_TYPE.ADRESSE:
          if (!vCard.labeledAddresses) vCard.labeledAddresses = [];
          vCard.labeledAddresses.push({
            label: item.title,
            street: item.strasse,
            city: item.ort,
            postalCode: item.plz,
            stateProvince: item.bundesland || '',
            countryRegion: item.land || '',
          });
          break;
        case LINK_TYPE.EMAIL_ARBEIT:
          if (!vCard.workEmail) vCard.workEmail = [];
          vCard.workEmail.push(item.email);
          break;
        case LINK_TYPE.EMAIL_PRIVAT:
          if (!vCard.email) vCard.email = [];
          vCard.email.push(item.email);
          break;
        case LINK_TYPE.EMAIL:
          if (!vCard.labeledEmails) vCard.labeledEmails = [];
          vCard.labeledEmails.push({
            label: item.title,
            email: item.email,
          });
          break;
        case LINK_TYPE.TELEFON_ARBEIT:
          if (!vCard.workPhone) vCard.workPhone = [];
          vCard.workPhone.push(item.telefonnummer);
          break;
        case LINK_TYPE.TELEFON_PRIVAT:
          if (!vCard.cellPhone) vCard.cellPhone = [];
          vCard.cellPhone.push(item.telefonnummer);
          break;
        case LINK_TYPE.TELEFON:
          // Neue Logik für Labels
          if (!vCard.labeledPhones) vCard.labeledPhones = [];
          vCard.labeledPhones.push({
            label: item.title,
            number: item.telefonnummer,
          });
          break;
        case LINK_TYPE.SOCIAL_MEDIA:
          if (!vCard.socialUrls) vCard.socialUrls = {};
          vCard.socialUrls[item.netzwerk] = 'https://' + item.link;
          break;
        case LINK_TYPE.GEBURTSTAG:
          vCard.birthday = new Date(item.geburtstag);
          break;
        case LINK_TYPE.NOTIZEN:
          if (!vCard.note) vCard.note = '';
          vCard.note += item.title + ': ' + item.link + '\n';
          break;
        case LINK_TYPE.LINK:
          if (!vCard.url) vCard.url = {};
          vCard.url[item.title] = item.link;
          break;
        default:
          break;
      }
    });

    // Fix für iOS Social Media Links: Zeigt nur CHARSET=UTF-8 an.
    let vCardString = vCard.getFormattedString();
    vCardString = vCardString.replace(
      /SOCIALPROFILE;CHARSET=UTF-8;/gm,
      'SOCIALPROFILE;'
    );
    // console.log(vCardString);
    return vCardString;
    // return vCard.getFormattedString();
  };

  return (
    <button
      type="button"
      className="inline-flex items-center gap-x-2 rounded-md bg-white px-3.5 py-2.5 text-lg focus-visible:outline focus-visible:outline-offset-2 shadow-lg hover:shadow-sm transition-all"
      style={{ color: textColor }}
      onClick={async () => {
        const vcfText = await CreateVCard();
        downloadTxtFile(vcfText);
        trackProfileDownload(ownerId);
      }}
    >
      Kontakt hinzufügen
      <UserPlusIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
    </button>
  );
};

export default profileDownloader;
